import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";

const GUIDES = {
  guest: { file: "/docs/guide-guest.md", title: "Guest Guide" },
  member: { file: "/docs/guide-member.md", title: "Member Guide" },
  finance_admin: { file: "/docs/guide-finance-admin.md", title: "Finance Admin Guide" },
  accountant: { file: "/docs/guide-accountant.md", title: "Accountant Guide" },
  merchant: { file: "/docs/guide-merchant.md", title: "Merchant Guide" },
  shop_admin: { file: "/docs/guide-shop-admin.md", title: "Shop Admin Guide" },
  admin: { file: "/docs/guide-system-admin.md", title: "System Admin Guide" },
};

export default function HelpPage() {
  const [content, setContent] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const role = searchParams.get("role") || "guest";
  const guide = GUIDES[role] || GUIDES.guest;

  useEffect(() => {
    fetch(guide.file)
      .then(r => r.text())
      .then(md => setContent(md))
      .catch(() => setContent("# Documentation not found\n\nPlease select a role."));
  }, [role, guide.file]);

  const renderMd = (md) => {
    return md
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-[#1e3a5f] mt-5 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-extrabold text-[#1e3a5f] mt-8 mb-3 pb-2 border-b border-slate-200">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-extrabold text-[#1e3a5f] mb-2">$1</h1>')
      .replace(/^> (.+)$/gm, '<div class="border-l-4 border-[#1e3a5f] pl-4 italic text-slate-500 my-3">$1</div>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-slate-100 px-1.5 py-0.5 rounded text-sm font-mono text-[#1e3a5f]">$1</code>')
      .replace(/^\d+\. (.+)$/gm, '<li class="ml-6 list-decimal text-sm text-slate-700 leading-relaxed mb-1">$1</li>')
      .replace(/^- (.+)$/gm, '<li class="ml-6 list-disc text-sm text-slate-700 leading-relaxed mb-1">$1</li>')
      .replace(/^---$/gm, '<hr class="my-6 border-slate-200" />')
      .replace(/\| (.+) \|/gm, (match) => {
        if (match.includes("---")) return "";
        const cells = match.split("|").filter(c => c.trim()).map(c => `<td class="px-3 py-2 border border-slate-200 text-sm">${c.trim()}</td>`).join("");
        return `<tr class="even:bg-slate-50">${cells}</tr>`;
      })
      .replace(/(<tr.*<\/tr>(\s*<tr.*<\/tr>)+)/g, '<table class="w-full border-collapse border border-slate-200 rounded-lg my-3">$1</table>')
      .replace(/\*\*Q: (.+?)\*\*/g, '<p class="font-bold text-sm text-[#1e3a5f] mt-3">Q: $1</p>')
      .replace(/\*\*A: (.+)/g, '<p class="text-sm text-slate-600 mb-2">A: $1</p>')
      .replace(/\n\n/g, "<br/>");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1 rounded-full hover:bg-slate-100"><ArrowLeft className="w-5 h-5 text-slate-500" /></button>
            <img src="/mymaitree-logo.png" alt="My Maitree" className="h-6 w-auto" onError={(e) => { e.target.style.display = "none"; }} />
            <h1 className="font-bold text-sm text-[#1e3a5f]">{guide.title}</h1>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-6">
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMd(content)) }} />
        <p className="text-center text-slate-400 text-xs mt-12">Maitree e.V. — my.maitree-ev.org</p>
      </div>
    </div>
  );
}
